import React, { useRef, useMemo, useContext, useCallback } from 'react';
import tabbable from 'tabbable';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var hiddenTabbableStyle = {
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
  pointerEvents: 'none',
  zIndex: -1
};
var HiddenTabbable = React.forwardRef(function (_ref, ref) {
  var _ref$as = _ref.as,
      ElementType = _ref$as === void 0 ? 'span' : _ref$as,
      rest = _objectWithoutProperties(_ref, ["as"]);

  return React.createElement(ElementType, _extends({
    ref: ref,
    tabIndex: 0,
    style: hiddenTabbableStyle
  }, rest));
});
HiddenTabbable.displayName = 'HiddenTabbable';

function useTabPortal() {
  var contentRef = useRef();
  var portalRef = useRef();
  return useMemo(function () {
    return {
      contentRef: contentRef,
      portalRef: portalRef
    };
  }, []);
}

function getTabbableNodes(parentNode) {
  return tabbable(parentNode);
}
function focusAfter(node) {
  var tabItems = getTabbableNodes(document.body);
  var nodeIndex = tabItems.indexOf(node);

  if (nodeIndex !== -1) {
    var nextIndex = nodeIndex + 1;

    if (nextIndex < tabItems.length) {
      var nextTab = tabItems[nextIndex];
      nextTab.focus();
      return true;
    }
  }

  return false;
}
function focusBefore(node) {
  var tabItems = getTabbableNodes(document.body);
  var nodeIndex = tabItems.indexOf(node);

  if (nodeIndex !== -1) {
    var prevIndex = nodeIndex - 1;

    if (prevIndex >= 0) {
      var prevTab = tabItems[prevIndex];
      prevTab.focus();
      return true;
    }
  }

  return false;
}
function didMoveBackward(target, relatedTarget) {
  return relatedTarget ? Boolean(target.compareDocumentPosition(relatedTarget) & Node.DOCUMENT_POSITION_FOLLOWING) : false;
}

var Context = React.createContext();

function Portal(_ref) {
  var tabPortal = _ref.to,
      rest = _objectWithoutProperties(_ref, ["to"]);

  var context = useContext(Context);

  if (!tabPortal) {
    tabPortal = context;
  }

  var _tabPortal = tabPortal,
      contentRef = _tabPortal.contentRef,
      portalRef = _tabPortal.portalRef;
  var handleFocus = useCallback(function (event) {
    event.stopPropagation();
    var focused = false;
    var contentNode = contentRef.current;

    if (contentNode) {
      var tabItems = getTabbableNodes(contentNode);
      var isMovingBackward = didMoveBackward(event.currentTarget, event.relatedTarget);

      if (tabItems.length > 2) {
        var focusIndex = isMovingBackward ? tabItems.length - 2 : 1;
        tabItems[focusIndex].focus();
        focused = true;
      } else {
        if (isMovingBackward) {
          focused = focusBefore(portalRef.current);
        } else {
          focused = focusAfter(portalRef.current);
        }
      }
    }

    if (!focused) {
      event.currentTarget.blur();
    }
  }, [contentRef, portalRef]);
  return React.createElement(HiddenTabbable, _extends({}, rest, {
    ref: portalRef,
    onFocus: handleFocus
  }));
}

Portal.displayName = 'TabPortal.Portal';
var Content = React.forwardRef(function (_ref2, inputRef) {
  var _ref2$as = _ref2.as,
      ElementType = _ref2$as === void 0 ? 'div' : _ref2$as,
      children = _ref2.children,
      tabPortal = _ref2.from,
      hiddenTabbableAs = _ref2.hiddenTabbableAs,
      rest = _objectWithoutProperties(_ref2, ["as", "children", "from", "hiddenTabbableAs"]);

  var headRef = useRef();
  var tailRef = useRef();
  var context = useContext(Context);

  if (!tabPortal) {
    tabPortal = context;
  }

  var _tabPortal2 = tabPortal,
      contentRef = _tabPortal2.contentRef,
      portalRef = _tabPortal2.portalRef;
  var handleFocusHead = useCallback(function (event) {
    event.stopPropagation();
    var focused = false;
    var contentNode = contentRef.current;

    if (event.relatedTarget === tailRef.current) {
      focused = focusBefore(headRef.current);
    } else if (event.relatedTarget && contentNode.contains(event.relatedTarget)) {
      // If focus moved here from inside the content, we're moving backward
      // and should thus move focus back to the last tabbable element before
      // the portal.
      focused = focusBefore(portalRef.current);
    } else {
      // Otherwise, it means we've tabbed forward from the last element
      // before the content, and we should skip to the first tabbable
      // element after the content.
      focused = focusAfter(tailRef.current);
    }

    if (!focused) {
      event.currentTarget.blur();
    }
  }, [contentRef, portalRef]);
  var handleFocusTail = useCallback(function (event) {
    event.stopPropagation();
    var focused = false;

    if (didMoveBackward(event.currentTarget, event.relatedTarget)) {
      // If focus moved here from after this element in the document order,
      // we're moving backward and should thus skip to the last tabbable
      // element before the content.
      focused = focusBefore(headRef.current);
    } else {
      // Otherwise, it means we've tabbed forward from inside the content,
      // and we should move focus back to the first tabbable element after
      // the portal.
      focused = focusAfter(portalRef.current);
    }

    if (!focused) {
      event.currentTarget.blur();
    }
  }, [portalRef]);
  var ref = useCallback(function (node) {
    contentRef.current = node;

    if (typeof inputRef === 'function') {
      inputRef(node);
    } else if (inputRef) {
      inputRef.current = node;
    }
  }, [inputRef, contentRef]);
  return React.createElement(ElementType, _extends({
    ref: ref
  }, rest), React.createElement(HiddenTabbable // The "head" focusable item inside the content. It serves to
  // capture when focus moves to the beginning of the content, which
  // means the user has either tabbed forward to it from the previous
  // element in document order, or tabbed backward from an element
  // inside the content (like something in `children`, or the "tail").
  , {
    as: hiddenTabbableAs,
    ref: headRef,
    onFocus: handleFocusHead
  }), children, React.createElement(HiddenTabbable // The "tail" focusable item inside the content. It serves to
  // capture when focus has reached the end of the content, which
  // means the user has either tabbed backward to it from the next
  // element in document order, or tabbed forward to it from an element
  // inside the content (like something in `children`, or the "head").
  , {
    as: hiddenTabbableAs,
    ref: tailRef,
    onFocus: handleFocusTail
  }));
});
Content.displayName = 'TabPortal.Content';

function TabPortal(_ref3) {
  var children = _ref3.children;
  var value = useTabPortal();
  return React.createElement(Context.Provider, {
    value: value
  }, children);
}

TabPortal.Portal = Portal;
TabPortal.Content = Content;

export { HiddenTabbable, TabPortal, hiddenTabbableStyle, useTabPortal };
